import React from 'react';
import { Button } from '@hud/web-components/Button/Button';
import useEndpointSettingsMutation from '../../../hooks/useEndpointSettingsMutation';
import {
  NotificationType,
  showNotification,
} from '../../../components/ToastNotification/ToastNotification';
import {
  EndpointSettingsAction,
  EndpointsSettings,
} from '@hud/portal-dtos/issues.dtos';
import {
  SettingsUpdatedErrorMessage,
  SettingsUpdatedSuccessfullyMessage,
} from '../../../components/IssuesMenuWebappContainer/IssuesMenuWebappContainer';
import { EndpointSettingsTableMode } from './EndpointSettingsTable';
import useAnalytics from '../../../hooks/useAnalytics';

interface EndpointSettingsActionCellProps {
  settings: EndpointsSettings;
  tableMode: EndpointSettingsTableMode;
}

export function EndpointSettingsActionCell({
  settings,
  tableMode,
}: EndpointSettingsActionCellProps) {
  const endpointSettingsMutation = useEndpointSettingsMutation();
  const analytics = useAnalytics();

  const isMuted = settings.isMuted;
  const isDetectionExcluded = settings.isDetectionExcluded;

  const text = getActionText(tableMode, isMuted, isDetectionExcluded);

  async function updateState() {
    const endpointId = settings.endpointId;
    const serviceId = settings.serviceId;

    try {
      if (shouldResumeMonitoring(tableMode, isDetectionExcluded)) {
        analytics.capture('settings_heads_up_action_clicked', {
          endpoint_id: endpointId,
          service_id: serviceId,
          action: EndpointSettingsAction.INCLUDE_DETECTION,
        });
        await endpointSettingsMutation.mutateAsync({
          endpointId,
          serviceId,
          action: EndpointSettingsAction.INCLUDE_DETECTION,
        });
      }

      if (shouldUnmute(tableMode, isMuted)) {
        analytics.capture('settings_heads_up_action_clicked', {
          endpoint_id: endpointId,
          service_id: serviceId,
          action: EndpointSettingsAction.UNMUTE,
        });
        await endpointSettingsMutation.mutateAsync({
          endpointId,
          serviceId,
          action: EndpointSettingsAction.UNMUTE,
        });
      }

      showNotification({
        type: NotificationType.SUCCESS,
        message: (
          <SettingsUpdatedSuccessfullyMessage
            onClick={() => {
              analytics.capture('toast_action_clicked', {});
            }}
          />
        ),
      });
    } catch (error) {
      showNotification({
        type: NotificationType.ERROR,
        message: SettingsUpdatedErrorMessage,
      });
    }
  }

  return (
    <Button
      variant="text"
      disabled={endpointSettingsMutation.isPending}
      onClick={updateState}
    >
      {text}
    </Button>
  );
}

function shouldResumeMonitoring(
  tableMode: EndpointSettingsTableMode,
  isDetectionExcluded: boolean,
) {
  return isDetectionExcluded;
  // return (
  //   tableMode === EndpointSettingsTableMode.Unmonitored || isDetectionExcluded
  // );
}

function shouldUnmute(tableMode: EndpointSettingsTableMode, isMuted: boolean) {
  return tableMode === EndpointSettingsTableMode.Muted && isMuted;
}

function getActionText(
  tableMode: EndpointSettingsTableMode,
  isMuted: boolean,
  isDetectionExcluded: boolean,
) {
  // For 'Unmonitored endpoints' table there is only one action - 'Resume'
  if (tableMode === EndpointSettingsTableMode.Unmonitored) {
    return 'Resume';
  }

  // For 'Muted endpoints' table there are two actions:
  if (isDetectionExcluded) {
    return 'Unmute and Start monitoring';
  }
  return 'Unmute';
}
